import type { Tenant } from './types';

/**
 * Datos fiscales del cliente: lo que hace falta para poder emitirle una
 * factura.
 *
 * Por que existe este fichero. Alexia Rest cobra por Stripe, pero Stripe es
 * solo la pasarela: quien presta el servicio, y por tanto quien factura, es
 * Polar Consulting Group 33 SL. Sin NIF, razon social y direccion no se puede
 * emitir una factura valida, y hasta ahora el alta solo pedia el nombre del
 * restaurante y el pais.
 *
 * Lo que este fichero NO hace: decidir cuanto IVA se repercute. Eso depende de
 * donde este el cliente, de si tiene NIF-IVA intracomunitario y de umbrales
 * que cambian; lo lleva la gestoria en Holded, no el producto. Aqui solo
 * recogemos y validamos el dato, y calculamos el REGIMEN (quien es el cliente
 * a efectos de IVA) para que la lista de facturacion diga a las claras que
 * tratamiento toca.
 */

/** Paises de la UE, para distinguir intracomunitario de exportacion. */
export const PAISES_UE = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
  'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
  'SE', 'SI', 'SK',
] as const;

export type PaisUE = (typeof PAISES_UE)[number];

export const esPaisUE = (pais: string): boolean =>
  (PAISES_UE as readonly string[]).includes(pais.trim().toUpperCase());

/**
 * Regimen de IVA aplicable. Son los cuatro casos que confirmo la gestoria;
 * el nombre describe QUIEN es el cliente, no el tipo que se aplica, porque
 * el tipo puede cambiar y el caso no.
 */
export type RegimenIva =
  /** Cliente en Espana: IVA espanol. */
  | 'nacional'
  /** Empresa de otro pais UE con NIF-IVA valido: inversion del sujeto pasivo. */
  | 'intracomunitario_empresa'
  /** Cliente UE sin NIF-IVA: IVA espanol mientras no se supere el umbral. */
  | 'intracomunitario_consumidor'
  /** Fuera de la UE: no sujeto a IVA espanol. */
  | 'extracomunitario';

export const ETIQUETA_REGIMEN: Record<RegimenIva, string> = {
  nacional: 'España · IVA 21 %',
  intracomunitario_empresa: 'UE con NIF-IVA · inversión del sujeto pasivo',
  intracomunitario_consumidor: 'UE sin NIF-IVA · IVA español',
  extracomunitario: 'Fuera de la UE · No sujeto',
};

export interface TaxProfile {
  /** Razon social o nombre fiscal. Puede no coincidir con el del restaurante. */
  legalName?: string;
  /** NIF/CIF, NIF-IVA intracomunitario, RFC... segun el pais. */
  taxId?: string;
  /** Pais fiscal en ISO 3166-1 alfa-2. Puede diferir del pais del negocio. */
  taxCountry?: string;
  addressLine?: string;
  postalCode?: string;
  city?: string;
  province?: string;
}

/**
 * Que regimen le toca a este cliente. `taxIdValido` se pasa aparte porque un
 * NIF-IVA mal escrito NO convierte a una empresa en consumidor: hasta que se
 * corrija, el caso queda sin resolver y quien factura decide.
 */
export function regimenIvaDe(profile: TaxProfile, paisNegocio: string): RegimenIva {
  const pais = (profile.taxCountry || paisNegocio || '').trim().toUpperCase();
  if (pais === 'ES') return 'nacional';
  if (!esPaisUE(pais)) return 'extracomunitario';
  // En la UE, la diferencia la marca tener NIF-IVA intracomunitario, que es el
  // que empieza por el codigo del pais.
  return tieneFormatoNifIva(profile.taxId ?? '', pais)
    ? 'intracomunitario_empresa'
    : 'intracomunitario_consumidor';
}

/* ------------------------------------------------------------------ */
/* Validacion del identificador fiscal                                 */
/* ------------------------------------------------------------------ */

const limpiar = (valor: string): string => valor.toUpperCase().replace(/[\s.\-/]/g, '');

/** Letra de control del NIF/NIE espanol. El orden importa y no es alfabetico. */
const LETRAS_NIF = 'TRWAGMYFPDXBNJZSQVHLCKE';
/** Letras de control de los CIF de sociedades. */
const LETRAS_CIF = 'JABCDEFGHI';

/** DNI: 8 cifras + letra calculada como el resto entre 23. */
function validarDni(valor: string): boolean {
  if (!/^\d{8}[A-Z]$/.test(valor)) return false;
  return LETRAS_NIF[Number(valor.slice(0, 8)) % 23] === valor[8];
}

/** NIE: X/Y/Z equivalen a 0/1/2 y luego se calcula como un DNI. */
function validarNie(valor: string): boolean {
  if (!/^[XYZ]\d{7}[A-Z]$/.test(valor)) return false;
  const numero = String('XYZ'.indexOf(valor[0])) + valor.slice(1, 8);
  return LETRAS_NIF[Number(numero) % 23] === valor[8];
}

/**
 * CIF de sociedad: letra de tipo + 7 cifras + digito o letra de control.
 * El control se calcula duplicando las posiciones pares y sumando las cifras
 * del resultado (el mismo esquema que una tarjeta de credito).
 */
function validarCif(valor: string): boolean {
  if (!/^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/.test(valor)) return false;
  const cifras = valor.slice(1, 8);
  let suma = 0;
  for (let i = 0; i < 7; i += 1) {
    const n = Number(cifras[i]);
    if (i % 2 === 0) {
      const doble = n * 2;
      suma += Math.floor(doble / 10) + (doble % 10);
    } else {
      suma += n;
    }
  }
  const control = (10 - (suma % 10)) % 10;
  const ultimo = valor[8];
  // Unas letras de tipo exigen letra de control, otras digito y el resto
  // aceptan las dos.
  if ('PQRSNW'.includes(valor[0])) return ultimo === LETRAS_CIF[control];
  if ('ABEH'.includes(valor[0])) return ultimo === String(control);
  return ultimo === String(control) || ultimo === LETRAS_CIF[control];
}

/** Identificador fiscal espanol: DNI, NIE o CIF. */
export function validarNifEspanol(valor: string): boolean {
  const v = limpiar(valor);
  return validarDni(v) || validarNie(v) || validarCif(v);
}

/**
 * Longitudes y formas del NIF-IVA de cada pais de la UE. No comprueba digitos
 * de control (cada pais tiene el suyo): solo que la forma sea plausible. La
 * validez REAL solo la da el VIES, y eso se consulta desde la API.
 */
const FORMATO_NIF_IVA: Record<string, RegExp> = {
  AT: /^U\d{8}$/, BE: /^[01]\d{9}$/, BG: /^\d{9,10}$/, CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/, DE: /^\d{9}$/, DK: /^\d{8}$/, EE: /^\d{9}$/,
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/, FI: /^\d{8}$/, FR: /^[A-Z0-9]{2}\d{9}$/,
  GR: /^\d{9}$/, HR: /^\d{11}$/, HU: /^\d{8}$/, IE: /^(\d{7}[A-W][A-I]?|[0-9A-Z]\d{5}[A-W])$/,
  IT: /^\d{11}$/, LT: /^(\d{9}|\d{12})$/, LU: /^\d{8}$/, LV: /^\d{11}$/,
  MT: /^\d{8}$/, NL: /^\d{9}B\d{2}$/, PL: /^\d{10}$/, PT: /^\d{9}$/,
  RO: /^\d{2,10}$/, SE: /^\d{12}$/, SI: /^\d{8}$/, SK: /^\d{10}$/,
};

/** ¿Tiene forma de NIF-IVA intracomunitario del pais indicado? */
export function tieneFormatoNifIva(valor: string, pais: string): boolean {
  const v = limpiar(valor);
  const codigo = pais.trim().toUpperCase();
  const formato = FORMATO_NIF_IVA[codigo];
  if (!formato) return false;
  // El NIF-IVA lleva delante el codigo del pais; sin el no es intracomunitario.
  if (!v.startsWith(codigo)) return false;
  return formato.test(v.slice(2));
}

/** Normaliza un NIF-IVA a la forma que espera el VIES: sin espacios ni guiones. */
export const normalizarTaxId = (valor: string): string => limpiar(valor);

/** RFC mexicano: 3 letras (moral) o 4 (fisica) + fecha + homoclave. */
export function validarRfcMexicano(valor: string): boolean {
  return /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(limpiar(valor));
}

export interface ResultadoValidacion {
  ok: boolean;
  /** Mensaje para el usuario. Vacio cuando ok. */
  message?: string;
  /** true cuando no sabemos validar ese pais y lo damos por bueno. */
  sinComprobar?: boolean;
}

/**
 * Valida el identificador fiscal segun el pais.
 *
 * Regla de oro: NUNCA bloquea. Un identificador que no reconocemos se acepta
 * con aviso. Preferimos un dato raro que se corrige al facturar antes que un
 * restaurante peruano que no puede darse de alta porque no sabemos como es su
 * RUC. Lo unico que se rechaza de plano es lo que es claramente un error de
 * tecleo en un pais que SI sabemos validar.
 */
export function validarTaxId(valor: string, pais: string): ResultadoValidacion {
  const v = limpiar(valor);
  const codigo = (pais || '').trim().toUpperCase();
  if (v === '') return { ok: true };

  if (codigo === 'ES') {
    if (validarNifEspanol(v)) return { ok: true };
    // Un NIF-IVA espanol (ESB75764274) tambien es valido escrito asi.
    if (v.startsWith('ES') && validarNifEspanol(v.slice(2))) return { ok: true };
    return {
      ok: false,
      message: 'Ese NIF/CIF no es válido. Revisa la letra: no cuadra con el número.',
    };
  }

  if (codigo === 'MX') {
    if (validarRfcMexicano(v)) return { ok: true };
    return {
      ok: false,
      message: 'Ese RFC no tiene el formato correcto (por ejemplo, ABC010101AB1).',
    };
  }

  if (esPaisUE(codigo)) {
    if (tieneFormatoNifIva(v, codigo)) return { ok: true };
    // Sin el prefijo del pais no es intracomunitario, pero puede ser un
    // identificador nacional perfectamente valido: se acepta con aviso.
    return {
      ok: true,
      sinComprobar: true,
      message: `Para facturar sin IVA hace falta el NIF-IVA intracomunitario, que empieza por ${codigo} (p. ej. ${codigo}12345678). Si no lo tienes, la factura irá con IVA.`,
    };
  }

  return { ok: true, sinComprobar: true };
}

/* ------------------------------------------------------------------ */
/* Completitud                                                         */
/* ------------------------------------------------------------------ */

/**
 * Nombre local del identificador fiscal: en Mexico nadie llama "NIF" al RFC.
 * Se usa en el formulario, en los avisos de datos faltantes y en las
 * validaciones (identificacion fiscal dinamica, ronda 3).
 */
export function etiquetaTaxId(country: string | undefined): string {
  const pais = (country ?? '').trim().toUpperCase();
  if (pais === 'MX') return 'RFC';
  if (pais === 'ES') return 'NIF';
  if (pais === 'US') return 'EIN';
  return 'Identificación fiscal';
}

/** Campos imprescindibles para emitir una factura. */
const OBLIGATORIOS: Array<{ campo: keyof TaxProfile; etiqueta: string }> = [
  { campo: 'legalName', etiqueta: 'Razón social' },
  { campo: 'taxId', etiqueta: 'NIF / identificación fiscal' },
  { campo: 'addressLine', etiqueta: 'Dirección' },
  { campo: 'postalCode', etiqueta: 'Código postal' },
  { campo: 'city', etiqueta: 'Ciudad' },
  { campo: 'taxCountry', etiqueta: 'País' },
];

/** Que le falta a este cliente para poder facturarle. Vacio = completo. */
export function camposFiscalesQueFaltan(profile: TaxProfile | undefined): string[] {
  const etiquetaDe = (campo: keyof TaxProfile, porDefecto: string) =>
    campo === 'taxId' ? etiquetaTaxId(profile?.taxCountry) : porDefecto;
  if (!profile) return OBLIGATORIOS.map((o) => etiquetaDe(o.campo, o.etiqueta));
  return OBLIGATORIOS.filter((o) => (profile[o.campo] ?? '').toString().trim() === '').map(
    (o) => etiquetaDe(o.campo, o.etiqueta),
  );
}

export const puedeFacturarse = (profile: TaxProfile | undefined): boolean =>
  camposFiscalesQueFaltan(profile).length === 0;

/**
 * Resumen para la lista de facturacion del panel: quien puede facturarse, con
 * que regimen, y a quien hay que perseguir.
 */
export interface EstadoFacturacion {
  tenantId: string;
  name: string;
  legalName?: string;
  taxId?: string;
  regimen: RegimenIva;
  faltan: string[];
  facturable: boolean;
}

export function estadoFacturacionDe(
  tenant: Pick<Tenant, 'tenantId' | 'name' | 'country'> & { tax?: TaxProfile },
): EstadoFacturacion {
  const tax = tenant.tax;
  const faltan = camposFiscalesQueFaltan(tax);
  return {
    tenantId: tenant.tenantId,
    name: tenant.name,
    legalName: tax?.legalName,
    taxId: tax?.taxId,
    regimen: regimenIvaDe(tax ?? {}, tenant.country),
    faltan,
    facturable: faltan.length === 0,
  };
}

/* ------------------------------------------------------------------ */
/* Puerta al checkout                                                  */
/* ------------------------------------------------------------------ */

/**
 * Por que se piden los datos ANTES de pagar y no despues.
 *
 * El Checkout de Stripe ya exige direccion y, en los paises cuyo identificador
 * conoce (NIF espanol, RFC mexicano), tambien el identificador; y
 * `fusionarDatosDeCheckout` los vuelca en el tenant. Con eso solo, sin embargo,
 * quedan tres huecos que se pagan al facturar:
 *
 * 1. La razon social. Stripe pide "Nombre" en la direccion de facturacion, que
 *    el 90 % rellena con el nombre de quien tiene la tarjeta delante. Una
 *    factura a «Pol Vales» cuando el cliente es «Cortijo Gastronomia SL» no
 *    sirve.
 * 2. Los paises cuyo identificador Stripe no sabe pedir: ahi no pide nada y la
 *    ficha se queda sin NIF.
 * 3. La validacion es la nuestra: Stripe acepta cualquier cosa con forma de
 *    RFC, y aqui se comprueba de verdad y se llama al campo por su nombre
 *    local.
 *
 * De ahi que esto bloquee el checkout. Es la unica cosa de este fichero que
 * dice "no": el resto avisa y deja pasar.
 */
export interface PuertaFacturacion {
  /** true cuando se puede ir al checkout. */
  ok: boolean;
  /** Campos vacios, con su etiqueta. Vacio no implica `ok`: el NIF puede estar mal. */
  faltan: string[];
  /** Un solo mensaje listo para enseñar. Vacio cuando `ok`. */
  message?: string;
}

/**
 * ¿Tiene este cliente lo justo para poder emitirle una factura?
 *
 * Rechaza dos cosas y solo dos: que falte un campo obligatorio, y que el
 * identificador fiscal sea claramente erroneo en un pais que sabemos validar.
 * Un identificador que no reconocemos sigue pasando (regla de `validarTaxId`).
 */
export function puertaFacturacion(profile: TaxProfile | undefined): PuertaFacturacion {
  const faltan = camposFiscalesQueFaltan(profile);
  if (faltan.length > 0) {
    const etiqueta = faltan.length === 1 ? 'este dato' : 'estos datos';
    return {
      ok: false,
      faltan,
      message:
        `Antes de contratar necesitamos ${etiqueta} para poder emitirte la factura: ` +
        `${faltan.join(', ')}.`,
    };
  }
  const veredicto = validarTaxId(profile?.taxId ?? '', profile?.taxCountry ?? '');
  if (!veredicto.ok) {
    return { ok: false, faltan: [], message: veredicto.message };
  }
  return { ok: true, faltan: [] };
}
